/**
 * Validation Context Provider
 *
 * Context provider for managing validation state across the admin interface.
 *
 * @since 1.0.0
 */

import { __ } from '@wordpress/i18n';
import React, { createContext, useCallback, useContext, useState } from 'react';

import type {
  ComponentValidationState,
  ValidationContextValue,
  ValidationProviderProps,
  ValidationRegistry,
} from './types';

const ValidationContext = createContext<ValidationContextValue | undefined>(undefined);

/**
 * Hook to access the validation context.
 *
 * @since 1.0.0
 *
 * @return Validation context value.
 */
export const useValidationContext = (): ValidationContextValue => {
  const context = useContext(ValidationContext);
  if (!context) {
    throw new Error(
      __('useValidationContext must be used within a ValidationProvider', 'redfox-added-to-cart-popup')
    );
  }
  return context;
};

/**
 * Validation provider component.
 *
 * @since 1.0.0
 *
 * @param props The component props.
 *
 * @return JSX element.
 */
export const ValidationProvider: React.FC<ValidationProviderProps> = ({ children }) => {
  const [validationRegistry, setValidationRegistry] = useState<ValidationRegistry>({});

  const registerValidation = useCallback(
    (componentKey: string, validationState: ComponentValidationState) => {
      setValidationRegistry((prev) => ({
        ...prev,
        [componentKey]: validationState,
      }));
    },
    []
  );

  const unregisterValidation = useCallback((componentKey: string) => {
    setValidationRegistry((prev) => {
      const { [componentKey]: _, ...rest } = prev;
      return rest;
    });
  }, []);

  const hasValidationErrors = useCallback((): boolean => {
    return Object.values(validationRegistry).some((state) => state.hasErrors);
  }, [validationRegistry]);

  const getAllValidationErrors = useCallback(() => {
    return Object.entries(validationRegistry)
      .filter(([, state]) => state.hasErrors)
      .map(([componentKey, state]) => ({
        componentKey,
        errors: state.errors,
      }));
  }, [validationRegistry]);

  const contextValue: ValidationContextValue = {
    validationRegistry,
    registerValidation,
    unregisterValidation,
    hasValidationErrors,
    getAllValidationErrors,
  };

  return <ValidationContext.Provider value={contextValue}>{children}</ValidationContext.Provider>;
};
